import { useCallback, useEffect, useState, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getStatus } from 'redux-resource';
import { Link, useNavigate } from 'react-router';
import { DateTime } from 'luxon';
import i18n from 'i18next';
import { coreActions, coreSelectors } from '@gymcloud/core';
import { uiActions } from 'web-actions';
import { Modal, Button, Avatar, Spinner, NotFound, ExerciseResultList } from 'web-components';
import { Flex, Text, SVG } from 'web-styled';
import * as S from './styles';

const RESOURCE_TYPE = 'workoutEvents' as const;

interface Props {
  id?: number;
  onRequestClose: () => void;
}

interface WorkoutNotesProps {
  note: string;
  onClose: () => void;
}

const WorkoutNotes = ({ note, onClose }: WorkoutNotesProps) => (
  <S.Notes>
    <S.NoteHeader>
      {`${i18n.t('Workout')} Notes`}
      <S.Close onClick={onClose}>Close</S.Close>
    </S.NoteHeader>
    <S.NotesBody>
      <Text whiteSpace="pre-line">{note}</Text>
    </S.NotesBody>
  </S.Notes>
);

interface EventStatusProps {
  readStatus: { pending: boolean; failed: boolean; succeeded: boolean };
  exercises: unknown[];
  id?: number;
}

const EventStatus = ({ readStatus, exercises, id }: EventStatusProps) => {
  if (readStatus.pending) return <Spinner message="Loading Event..." />;
  if (readStatus.failed) return <NotFound title={`Event Not Found (${id})`} />;
  if (readStatus.succeeded) return <ExerciseResultList exercises={exercises} />;
  return null;
};

const makeMapState = (id?: number) => (state: unknown) => ({
  readStatus: getStatus(
    state,
    [
      `${RESOURCE_TYPE}.requests.fetchFullEvent.status`,
      `${RESOURCE_TYPE}.meta[${id}].readStatus`,
    ],
    false,
  ),
  event: id ? coreSelectors.getCurrentEvent(RESOURCE_TYPE, id)(state) : {},
  exercises: id ? coreSelectors.getEventExercises(id)(state) : [],
});

const WorkoutResultModal = ({ id, onRequestClose }: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [notesVisible, setNotesVisible] = useState(false);

  const mapState = useCallback(makeMapState(id), [id]);
  const { event, exercises, readStatus } = useSelector(mapState, shallowEqual);

  useEffect(() => {
    if (id) dispatch(coreActions.requestLoadFullWorkoutEvent({ id }));
  }, [id]);

  const isFutureDate = useMemo(() => {
    if (!event.start) return false;
    return new Date(event.start).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0);
  }, [event.start]);

  const handleDelete = useCallback(() => {
    onRequestClose();
    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Remove All results?',
          bodyText: 'Are you sure you want remove all results?',
        },
        onConfirmAction: coreActions.requestDeleteEntity({ id: id!, resourceType: RESOURCE_TYPE }),
      }),
    );
  }, [id, onRequestClose]);

  const handleEditClick = useCallback(() => {
    dispatch(
      uiActions.setUiState({
        newWorkoutEventModal: {
          isOpen: true,
          initialValues: {
            originalId: event.id,
            begins_at: DateTime.fromISO(event.begins_at),
            ends_at: DateTime.fromISO(event.ends_at),
            person_id: event.user_id,
            personal_workout_id: event.personal_workout_id,
          },
        },
      }),
    );
    onRequestClose();
  }, [event, onRequestClose]);

  return (
    <Modal
      title={`${i18n.t('Workout')} Results`}
      isOpen={!!id}
      contentStyle={{ width: 600 }}
      onRequestClose={onRequestClose}
    >
      <Flex column width="100%" align="flex-start" spacing={25}>

        <S.Header spacing={14}>
          <Flex spacing={14}>
            <Link to={`/users/${event.person_id}`}>
              <Avatar
                src={event.avatar?.thumb?.url}
                width="80px"
                height="80px"
                borderColor="primary"
                borderWidth={3}
                placeholderSize={40}
              />
            </Link>
            <Flex column alignSelf="center" spacing={4}>
              <Text size="26px" color="#333333" weight={700} lineHeight="23px">
                {event.person_name}
              </Text>
              {event.program_name && (
                <Link to={`/personal_programs/${event.personal_program_id}`}>
                  <Text color="primary" size="16px">{event.program_name}</Text>
                </Link>
              )}
            </Flex>
          </Flex>

          {isFutureDate && (
            <Button ghost spacing="large" appearance="primary" onClick={handleEditClick}>
              Edit
            </Button>
          )}
        </S.Header>

        <S.SubHeader>
          <Flex column spacing={4}>
            <Link to={`/personal_workouts/${event.personal_workout_id}`}>
              <Text size="20px" color="primary" weight={700} lineHeight="23px">
                {event.workout_name}
              </Text>
            </Link>
            <Text color="#5F5F5F" size="17px">{event.start}</Text>
          </Flex>

          <S.NoteToggle
            disabled={!event.note}
            onClick={() => event.note && setNotesVisible(v => !v)}
          >
            See Notes
            <SVG.CopyFile />
          </S.NoteToggle>

          {notesVisible && event.note && (
            <WorkoutNotes note={event.note} onClose={() => setNotesVisible(false)} />
          )}
        </S.SubHeader>

        <EventStatus readStatus={readStatus} exercises={exercises} id={id} />

        <Flex width="100%" justify="space-between">
          <Button ghost spacing="large" appearance="danger" onClick={handleDelete}>
            Delete
          </Button>
          <Button ghost spacing="large" appearance="primary" onClick={() => navigate(`/events/${event.id}/results`)}>
            Start
          </Button>
        </Flex>

      </Flex>
    </Modal>
  );
};

export default WorkoutResultModal;
